import { memo, useState, useEffect } from "react";
import { Alert, AlertTitle, Button, Card, CardContent, CardHeader, Dialog, Typography, useMediaQuery } from "@mui/material";
import { CreditCard, Fingerprint, Refresh } from "@mui/icons-material";
import BlurredBackdrop from "../../../../components/BlurredBackdrop";
import DwollaACHSetup from "../../../../components/DwollaACHSetup";
import PlaidAccountVerification from "../../../../components/PlaidAccountVerification";
import { AccountVerificationStatus, UCAStatus } from "..";
import MemberService from "../../../../services/MemberService";
import DwollaService from "../../../../services/DwollaService";
import axios from "axios";

import "./PaymentMethodAlert.css";

const PaymentMethodAlert = memo(({ customerId, status, terms, verificationStatus, onSuccess, onClose }) => {

    const [ dialogOpen, setDialogOpen ] = useState(false);
    const [ organizationName, setOrganizationName ] = useState('Membership Organization');
    const [ organizationEmail, setOrganizationEmail ] = useState('(email not set)');
    const [ consentExpirationNotes, setConsentExpirationNotes ] = useState('');
    const [ consentStatus, setConsentStatus ] = useState(null);
    const [ loadingConsentStatus, setLoadingConsentStatus ] = useState(false);
    const [ reauthorizationConfig, setReauthorizationConfig ] = useState(null);
    
    const isMobileDevice = useMediaQuery(theme => theme.breakpoints.down('md'));

    useEffect(() => {
        const source = axios.CancelToken.source();
        
        MemberService.getOrganizationName(source.token)
            .then(data => {
                if (data) {
                    setOrganizationName(data);
                }
            })
            .catch(error => {
                if (!axios.isCancel(error)) {
                    console.error('Error fetching organization name:', error);
                }
            });
            
        MemberService.getOrganizationEmail(source.token)
            .then(data => {
                if (data) {
                    setOrganizationEmail(data);
                }
            })
            .catch(error => {
                if (!axios.isCancel(error)) {
                    console.error('Error fetching organization email:', error);
                }
            });

        MemberService.getConsentExpirationNotes(source.token)
            .then(data => {
                if (data) {
                    setConsentExpirationNotes(data);
                }
            })
            .catch(error => {
                if (!axios.isCancel(error)) {
                    console.error('Error fetching consent expiration notes:', error);
                }
            });

        // Check consent status if we have a customer ID and status is setup complete
        if (customerId && status?.id === UCAStatus.SETUP_COMPLETE) {
            setLoadingConsentStatus(true);
            DwollaService.getPlaidConsentStatus(customerId, source.token)
                .then(consentData => {
                    // Only set consent status if we actually get meaningful data
                    if (consentData && (consentData.hasConsent === true || consentData.hasConsent === false)) {
                        console.log('Consent status received:', consentData); // Debug log
                        setConsentStatus(consentData);
                    } else {
                        console.log('Invalid consent data received:', consentData); // Debug log
                        setConsentStatus(null);
                    }
                })
                .catch(error => {
                    if (!axios.isCancel(error)) {
                        console.error('Error fetching consent status:', error);
                        // On error, assume no consent issues
                        setConsentStatus(null);
                    }
                })
                .finally(() => {
                    setLoadingConsentStatus(false);
                });
        }
            
        return () => {
            source.cancel();
        };
    }, [customerId, status]);

    const handleOpen = () => setDialogOpen(true);

    const handleClose = (e, reason) => {
        if (reason !== "backdropClick") {
            setDialogOpen(false);
            setReauthorizationConfig(null);
            onClose();
        }
    }

    const handleExpiredConsentSetup = () => {
        // For expired consent, first clear the expired consent data, then open setup
        if (isConsentExpired && customerId) {
            setLoadingConsentStatus(true);
            DwollaService.clearExpiredConsent(customerId)
                .then(() => {
                    setDialogOpen(true);
                })
                .catch(error => {
                    console.error('Error clearing expired consent:', error);
                    // Even if clearing fails, still open the setup dialog
                    setDialogOpen(true);
                })
                .finally(() => {
                    setLoadingConsentStatus(false);
                });
        } else {
            setDialogOpen(true);
        }
    }

    const onPaymentSuccess = () => {
        handleClose();
        // Delays on success call to show closing effect
        setTimeout(() => {
            onSuccess();
        }, 200);
    }

    const handleReauthorization = () => {
        setLoadingConsentStatus(true);
        DwollaService.createPlaidReauthorizationToken(customerId)
            .then(({ linkToken, expiration }) => {
                setReauthorizationConfig({
                    linkToken,
                    expiration
                });
                setDialogOpen(true);
            })
            .catch(error => {
                console.error('Error creating reauthorization token:', error);
                // Fall back to regular setup if reauthorization fails
                setDialogOpen(true);
            })
            .finally(() => {
                setLoadingConsentStatus(false);
            });
    }

    const onReauthorizationSuccess = ({ public_token, accounts }) => {
        const source = axios.CancelToken.source();
        
        if (accounts && accounts.length > 0) {
            // Use the same flow as regular setup - it will update consent info
            DwollaService.createAndSetFundingSourceWithPlaidPublicToken(
                customerId, 
                public_token, 
                accounts[0]["id"], 
                accounts[0]["name"], 
                source.token
            )
            .then(() => {
                handleClose();
                onSuccess();
            })
            .catch(error => {
                console.error('Error during reauthorization:', error);
            });
        }
    }

    const onReauthorizationError = (error_metadata) => {
        console.error('Reauthorization error:', error_metadata);
        // Handle error appropriately
    }

    const onReauthorizationCancel = () => {
        setDialogOpen(false);
        setReauthorizationConfig(null);
    }

    // Determine if consent has expired
    // Only show consent-related messages if the user actually has consent info AND it's valid
    const hasValidConsentInfo = consentStatus?.hasConsent === true && 
                                consentStatus?.consentExpirationDate && 
                                consentStatus?.consentExpirationDate !== null;
    const isConsentExpired = hasValidConsentInfo && consentStatus?.consentExpired === true;
    const needsReauthorization = hasValidConsentInfo && consentStatus?.needsReauthorization === true;

    // Format expiration date
    const formatExpirationDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString();
    };

    const severity = (status?.id === UCAStatus.SETUP_COMPLETE && isConsentExpired) ? (
        "error"
    ) : (status?.id === UCAStatus.SETUP_COMPLETE && needsReauthorization) ? (
        "warning"
    ) : status?.id === UCAStatus.SETUP_COMPLETE ? (
        "success"
    ) : (status?.id === UCAStatus.AWAITING_VERIFICATION) ? (
        "info"
    ) : (status?.id === UCAStatus.ACCOUNT_SUSPENDED) ? (
        "error"
    ) : (
        "warning"
    );

    const buttonText = (status?.id === UCAStatus.SETUP_COMPLETE && isConsentExpired) ? (
        "Setup Payment Method"
    ) : (status?.id === UCAStatus.SETUP_COMPLETE && needsReauthorization) ? (
        "Reauthorize Now"
    ) : status?.id === UCAStatus.AWAITING_VERIFICATION ? (
        "Verify Now"
    ) : status?.id === UCAStatus.SETUP_COMPLETE ? (
        "Change Payment Method"
    ) : status?.id === UCAStatus.ACCOUNT_SUSPENDED ? (
        "Action Required"
    ) : (
        "Set up Now"
    );

    return (
    <>
        <Alert 
            className={`payment-alert ${(status?.id === UCAStatus.SETUP_COMPLETE && needsReauthorization) ? 'authorization-expiring' : ''}`}
            severity={severity}
            action={status?.id === UCAStatus.AWAITING_VERIFICATION 
                && (verificationStatus?.id === AccountVerificationStatus.AUTOMATED_MICRO_DEPOSIT_INITIATED || verificationStatus?.id === AccountVerificationStatus.NEEDS_MORE_INFORMATION) ? (
                // Do not show button if account is awaiting automated micro-deposits or is needing more information
                null
            ) : (
                <Button
                    disabled={status?.id === UCAStatus.ACCOUNT_SUSPENDED || loadingConsentStatus}
                    size="small"
                    disableElevation
                    onClick={(status?.id === UCAStatus.SETUP_COMPLETE && isConsentExpired) ? handleOpen : (status?.id === UCAStatus.SETUP_COMPLETE && needsReauthorization) ? handleReauthorization : handleOpen}
                    variant={status?.id === UCAStatus.AWAITING_VERIFICATION || (status?.id === UCAStatus.SETUP_COMPLETE && needsReauthorization) ? "contained" : "outlined"}
                    color={severity}
                    startIcon={status?.id === UCAStatus.AWAITING_VERIFICATION ? <Fingerprint /> : (status?.id === UCAStatus.SETUP_COMPLETE && needsReauthorization) ? <Refresh /> : <CreditCard /> }
                >
                    {buttonText}
                </Button>
            )}
        >
            {status?.id === UCAStatus.AWAITING_VERIFICATION ? (
                <>
                    <AlertTitle><b>New Account Pending Verification</b></AlertTitle>
                    It looks like your payment method is pending verification.
                    The verification deposit should post to your account within one business day but it can take up to 7 days.&nbsp;
                    {verificationStatus?.id === AccountVerificationStatus.MICRO_DEPOSIT_INITIATED ? (
                        <><b>Once you receive the micro-deposit</b>, click the button to verify account ownership.</>
                    ) : (
                        <>The verification will be done automatically and <b>no action is required</b> right now.</>
                    )}
                    <br/><br/>
                    <Typography variant="subtitle2">
                        <b>Verification Requested On:</b> {status?.lastUpdated.toLocaleDateString()}
                    </Typography>
                </>
            ) : status?.id === UCAStatus.SETUP_COMPLETE && loadingConsentStatus ? (
            <>
                <AlertTitle><b>Checking Authorization Status...</b></AlertTitle>
                Verifying your bank account authorization status.
            </>
            ) : status?.id === UCAStatus.SETUP_COMPLETE && isConsentExpired ? (
            <>
                <AlertTitle><b>Account Authorization Expired</b></AlertTitle>
                Your bank account authorization has expired and your payment method needs to be set up again. After the expiration date, you will need to re-start the bank setup process to maintain your membership.
            </>
            ) : status?.id === UCAStatus.SETUP_COMPLETE && needsReauthorization ? (
            <>
                <AlertTitle><b>Bank Account Authorization Expiring Soon</b></AlertTitle>
                The eDues authorization for your account at {consentStatus?.institutionName} is set to expire on {formatExpirationDate(consentStatus?.consentExpirationDate)}.
                You can reauthorize the account on file for another 12 months by clicking the "Reauthorize Now" button below.  If you fail to reauthorize your account by {formatExpirationDate(consentStatus?.consentExpirationDate)}, your current linked bank account will be removed.
                <br/><br/>
                <em>{consentExpirationNotes}</em>
            </>
            ) : status?.id === UCAStatus.SETUP_COMPLETE && !loadingConsentStatus ? (
            <>
                <AlertTitle><b>You are all set!</b></AlertTitle>
                It looks like your payment method is good to go.
            </>
            ) : status?.id === UCAStatus.ACCOUNT_SUSPENDED ? (
            <>
                <AlertTitle><b>Account Suspended</b></AlertTitle>
                We have received notification that your registered bank account on file for {organizationName} eDues has been <b><u>suspended</u></b>. 
                This may be due to action on your part or due to action by your financial institution. 
                This will prevent your local union from deducting your eDues automatically and can lead to the cancellation of your membership in the union. 
                Please contact <a href={`mailto:${organizationEmail}`}>{organizationEmail}</a> or your local union representative to address this issue as soon as possible.
            </>   
            ) : (
            <>
                <AlertTitle><b>Pending Payment Method Set-up</b></AlertTitle>
                It looks like you chose to pay your dues with ACH but you still need to set up your payment method.
            </>
            )}
        </Alert>
        {status?.id === UCAStatus.SETUP_COMPLETE && needsReauthorization && (
            <div style={{ 
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '8px',
                marginRight: '8px',
                fontSize: '14px'
            }}>
                <a 
                    href="#" 
                    onClick={(e) => {
                        e.preventDefault();
                        handleOpen();
                    }}
                    style={{ 
                        color: '#663C00', // Dark brown color
                        textDecoration: 'underline',
                        cursor: 'pointer'
                    }}
                >
                    Change bank account instead
                </a>
            </div>
        )}
        <Dialog 
            open={dialogOpen}
            BackdropComponent={BlurredBackdrop}
            onClose={handleClose}
            scroll="body"
            fullScreen={isMobileDevice}
            fullWidth
        >
            {/* If we have reauthorization config, show PlaidAccountVerification, otherwise show regular DwollaACHSetup */}
            {reauthorizationConfig ? (
                <Card variant="outlined">
                    <CardHeader title="Reauthorize Bank Account" subheader="Extend your authorization for another 12 months" />
                    <CardContent>
                        <PlaidAccountVerification 
                            linkToken={reauthorizationConfig.linkToken} 
                            onSuccess={onReauthorizationSuccess} 
                            onError={onReauthorizationError} 
                            onCancel={onReauthorizationCancel} 
                        />
                    </CardContent>
                </Card>
            ) : (
                <DwollaACHSetup 
                    dwollaCustomerId={customerId} 
                    paymentTerms={terms} 
                    onCancel={handleClose} 
                    onSuccess={onPaymentSuccess} 
                />
            )}
        </Dialog>
    </>
    )
})

export default PaymentMethodAlert;
